import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import {EntitySelectorComponent} from '../entity-selector/entity-selector.component';
import {Entity} from '../../models/entity';
import {Builder} from '../../models/builder';
import {of, Subject, Subscription} from 'rxjs';
import {debounceTime, flatMap, switchMap, tap} from 'rxjs/operators';
import Relator from '../../helpers/relator/relator';
import {ApiVirtualizationLayerService} from '../../services/api-virtualization-layer/api-virtualization-layer.service';

/**
 * This version does not use onCancel and onContinue.
 * This version supports lazyloading of the entities.
 * When the entities need to be lazyloaded, it cals the **loadMore()** output.
 * When the entities change, they pass on the change to the parent using the onSelectedEntitiesChange Output.
 *
 * Pass data to the "entities" input.
 * Get the selected data from the onContinue(EventEmitter) output.
 */
@Component({
  selector:    'ngx-message-entity-selector2',
  templateUrl: './message-entity-selector2.component.html',
  styleUrls:   ['./message-entity-selector2.component.scss'],
})
export class MessageEntitySelector2Component extends EntitySelectorComponent implements OnInit, OnDestroy, OnChanges {
  @Input() searchName: string       = '';
  @Input() searchMinAge: number     = 0;
  @Input() searchMaxAge: number     = 0;
  @Input() searchGroupId: number    = 0;
  @Input() searchLocationId: number = 0;

  @Output() loadMore: EventEmitter<boolean>                       = new EventEmitter();
  @Output() onSelectedEntitiesChange: EventEmitter<Array<Entity>> = new EventEmitter();

  public entitiesLoading: boolean          = true;
  public entitiesSearch$: Subject<boolean> = new Subject();

  public selectableCount = 0;
  public selectedCount   = 0;

  public selectingAll = false;

  private subEntities$          = new Subscription();
  private page                  = 1;
  private queryBuilder: Builder = new Builder();

  constructor(private apiv: ApiVirtualizationLayerService) {
    super();
  }

  ngOnInit() {
    super.ngOnInit();
//    this.queryBuilder.sort = new Sort('name', 'asc');
    this.loadEntities();
  }

  ngOnDestroy(): void {
    this.subEntities$.unsubscribe();
    this.selectAllSub.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges) {
    this.refilter();
  }

  refilter() {
    this.entitiesSearch$.next(true);
  }

  loadEntities() {
    this.subEntities$ = this.entitiesSearch$.pipe(
      debounceTime(800),
      // distinctUntilChanged(),
      tap((reset) => {
        if (reset) {
          this.page               = 1;
          this.selectableEntities = [];
          this.selectableEntities = [...this.selectableEntities];
        }
        this.queryBuilder = new Builder();

        this.queryBuilder.params['page'] = this.page;
        this.entitiesLoading             = true;

        // if (this.searchMinAge && this.searchMaxAge) {
        //   this.queryBuilder.params['filter[age]'] = '[' + this.searchMinAge + ',' + this.searchMaxAge + ']';
        // } else {
        //   this.queryBuilder.params['filter[age]'] = null;
        // }

        this.queryBuilder.params['filter[name]'] = this.searchName;
      }),
      switchMap(() => {
        /**
         * The entities can be filtered by:
         * - All (when no Location is chosen)
         * - Location (when no Group is chosen)
         * - Group
         *
         * - Age Min
         * - Age Max
         */
        return this.queryEntities(this.queryBuilder);
      }),
      tap(entities => {
        this.addEntities(entities);
      }),
    ).subscribe();
    this.entitiesSearch$.next(true);
  }

  addEntities(entities) {
    if (entities) {
      Relator.mergeOnce(entities['data'], this.selectableEntities);
      this.selectableCount    = entities['meta']['total'];
      this.selectableEntities = [...this.selectableEntities];
      this.page++;
      this.entitiesLoading = false;
    }
  }

  selectEntities(entities) {
    if (entities) {
      Relator.mergeOnce(entities['data'], this.selectedEntities);
      this.selectedEntities      = [...this.selectedEntities];
      this.apiv.selectedEntities = this.selectedEntities;
    }
  }

  queryEntities(b: Builder) {
    if (this.searchGroupId) {
      return this.apiv.getEntitiesFromGroup(this.searchGroupId, b);
    }

    if (this.searchLocationId) {
      return this.apiv.getEntitiesFromLocation(this.searchLocationId, b);
    }

    return this.apiv.getEntities(b);
  }

  onSelectableEntitiesScrollDown() {
    this.entitiesSearch$.next(false);
  }

  selectChild(i, child) {
    Relator.addOnce(child, this.selectedEntities);
    this.apiv.selectedEntities = this.selectedEntities;
    this.onSelectedEntitiesChange.emit(this.selectedEntities); //New list of Entities will be forwarden to parent component
  }

  private selectAllSub = new Subscription();

  selectAll() {
    this.selectAllSub.unsubscribe();
    this.selectAllSub = this.recursiveSelect(1).subscribe();
  }

  stopSelectAll() {
    this.selectAllSub.unsubscribe();
    this.selectingAll = false;
  }

  private recursiveSelect(page) {
    const b                  = new Builder();
    b.params['page']         = page;
    b.params['filter[name]'] = this.searchName;

    this.selectingAll = true;

    return this.queryEntities(b).pipe(
      flatMap(entities => {
        if (entities['data'].length) {
          this.selectEntities(entities);
          this.selectedCount = this.selectedEntities.length;
          return this.recursiveSelect(page + 1);
        } else {
          this.selectingAll = false;
          return of({});
        }
      }),
    );
  }

  isSelected(entity: Entity) {
    return Relator.findKeyById(entity.id, this.selectedEntities) !== -1;
  }

  unselectChild(i, child) {
    this.selectedEntities.splice(i, 1);
    this.selectedCount--;
    this.onSelectedEntitiesChange.emit(this.selectedEntities); //New list of Entities will be forwarden to parent component
  }

  clearSelected() {
    this.selectedEntities = [];
    this.onSelectedEntitiesChange.emit(this.selectedEntities); //New list of Entities will be forwarden to parent component
  }
}

